import { MessageCircle, Plus, Wrench } from 'lucide-react';
import { Link } from 'react-router';
import type { GeneralChatDoc, TicketDoc } from '@sf/shared';
import { useAuth } from '../../app/authContext';
import { EmptyState, PageHeader, SkeletonList, UnreadDot } from '../../components/ui/Feedback';
import { t } from '../../lib/copy';
import { useCollection, useDocument } from '../../lib/firestoreHooks';
import type { Ticket } from '../../lib/types';
import { generalChatRef } from '../chat/api';
import { tenantTicketsQuery } from '../tickets/api';
import { TicketListItem } from '../tickets/TicketListItem';

const primaryLink =
  'inline-flex h-10 items-center gap-2 rounded-input bg-brand px-4 text-sm font-medium text-white hover:bg-brand-800';
const secondaryLink =
  'inline-flex h-10 items-center gap-2 rounded-input border border-stone bg-white px-4 text-sm font-medium text-ink hover:bg-mist';

export function TenantDashboard() {
  const { profile } = useAuth();
  const uid = profile!.id;
  const tickets = useCollection<TicketDoc>(tenantTicketsQuery(uid), `tickets-${uid}`);
  const chat = useDocument<GeneralChatDoc>(generalChatRef(uid), `general-${uid}`);
  const open = tickets.data.filter((x) => x.status === 'open' || x.status === 'in_progress').length;

  return (
    <div>
      <PageHeader
        title="My tickets"
        subtitle={tickets.loading ? undefined : `${open} open · ${tickets.data.length} total`}
        actions={
          <>
            <Link to="/app/chat" className={secondaryLink}>
              <MessageCircle className="size-4" aria-hidden />
              Chat with admin
              <UnreadDot count={chat.data?.unreadForTenant} />
            </Link>
            <Link to="/app/tickets/new" className={primaryLink}>
              <Plus className="size-4" aria-hidden />
              Raise ticket
            </Link>
          </>
        }
      />
      {tickets.loading ? (
        <SkeletonList label={t('loading.tickets')} />
      ) : tickets.data.length === 0 ? (
        <EmptyState
          icon={Wrench}
          message={t('empty.tickets.tenant')}
          action={
            <Link to="/app/tickets/new" className={primaryLink}>
              <Plus className="size-4" aria-hidden />
              Raise ticket
            </Link>
          }
        />
      ) : (
        <ul className="flex flex-col gap-3" aria-label="My tickets">
          {(tickets.data as Ticket[]).map((ticket) => (
            <TicketListItem
              key={ticket.id}
              ticket={ticket}
              to={`/app/tickets/${ticket.id}`}
              unread={ticket.unreadForTenant}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
